/**
 * useCompanyBranding — Public Branding-Hook.
 *
 * Verwaltet das Firmen-Branding (Name, Logo, Farben) mit localStorage-Cache
 * (TTL 1 h), damit oeffentliche Screens (TV-Ansicht, Login, Terminal) das
 * Logo verzoegerungsfrei beim Page-Load anzeigen koennen — auch auf TVs/
 * Tablets mit schwacher Verbindung.
 *
 * Strategie:
 *   1. Initial-State aus dem Cache lesen (falls vorhanden und < 1 h alt)
 *   2. Im useEffect frischen Wert vom Backend holen und Cache aktualisieren
 *   3. Bei Netzwerkfehler bleibt der zuletzt bekannte Wert erhalten
 *
 * Backend-Endpoint: GET /api/company/branding (public, kein Auth noetig)
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';

const STORAGE_KEY = 'company_branding';
const TTL_MS = 60 * 60 * 1000;
const DEFAULTS = {
  name: 'TeamHub',
  logo_url: null,
  primary_color: '#14532D',
  secondary_color: '#65A30D',
};

function readCache() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.ts && Date.now() - parsed.ts < TTL_MS && parsed.data) {
      return parsed.data;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeCache(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {
    /* ignore */
  }
}

export function useCompanyBranding() {
  const [branding, setBranding] = useState(() => readCache() || DEFAULTS);

  useEffect(() => {
    api
      .get('/company/branding')
      .then((res) => {
        if (res?.data) {
          const merged = { ...DEFAULTS, ...res.data };
          setBranding(merged);
          writeCache(merged);
        }
      })
      .catch(() => {
        /* keep cached/default branding */
      });
  }, []);

  return branding;
}

/**
 * Liefert eine absolute URL fuer ein relatives Logo (`/api/public/logo/...`)
 * oder gibt eine bereits absolute URL unveraendert zurueck.
 */
export function resolveLogoUrl(logoUrl) {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  return `${process.env.REACT_APP_BACKEND_URL}${logoUrl}`;
}
